/* global HTMLDivElement, KeyboardEvent */
// Evening Shutdown Modal (D21) — review today's tasks, mark done / defer to new date,
// write reflection, rate energy (1-5), then save DailyReview + call updateTask for deferred tasks.
import { useEffect, useRef, useState } from "react";
import { MoonStar, X } from "lucide-react";
import { addDaysIso, todayIso } from "../lib/date";
import type { LifeOsApi } from "../lib/life-os-contract";
import { useFocusTrap } from "../lib/focus-trap";
import { todaysReviewTasks } from "./today-rituals-derive";
import { EveningTaskReviewRow } from "./evening-task-review-row";
import type { TaskReviewState } from "./evening-task-review-row";

interface Props {
  api: LifeOsApi;
  onClose: () => void;
}

const ENERGY_LABELS: Record<number, string> = {
  1: "Kiệt sức", 2: "Mệt", 3: "Bình thường", 4: "Tốt", 5: "Tuyệt vời",
};

export function EveningShutdownModal({ api, onClose }: Props) {
  const today = todayIso();
  const tasks = todaysReviewTasks(api.state, today);
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Escape closes the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Per-task review state: pre-populate done from current task status
  const [taskStates, setTaskStates] = useState<Record<string, TaskReviewState>>(() => {
    const init: Record<string, TaskReviewState> = {};
    for (const t of tasks) {
      init[t.id] = { done: t.status === "done", defer: false, deferDate: addDaysIso(1) };
    }
    return init;
  });

  const [reflection, setReflection] = useState(() => {
    return api.state.dailyReviews.find((r) => r.id === today)?.reflection ?? "";
  });
  const [energy, setEnergy] = useState<number | undefined>(() => {
    return api.state.dailyReviews.find((r) => r.id === today)?.energy;
  });

  function setTaskDone(taskId: string, done: boolean) {
    setTaskStates((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], done, defer: done ? false : prev[taskId].defer },
    }));
  }

  function setTaskDefer(taskId: string, defer: boolean) {
    setTaskStates((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], defer, done: defer ? false : prev[taskId].done },
    }));
  }

  function setDeferDate(taskId: string, date: string) {
    setTaskStates((prev) => ({ ...prev, [taskId]: { ...prev[taskId], deferDate: date } }));
  }

  function save() {
    const doneTaskIds: string[] = [];
    const deferredTaskIds: string[] = [];

    for (const t of tasks) {
      const s = taskStates[t.id];
      if (!s) continue;
      if (s.done) {
        doneTaskIds.push(t.id);
        if (t.status !== "done") {
          api.actions.updateTask(t.id, { status: "done", completedAt: new Date().toISOString() });
        }
      } else if (s.defer && s.deferDate && s.deferDate > today) {
        deferredTaskIds.push(t.id);
        api.actions.updateTask(t.id, { dueDate: s.deferDate });
      }
    }

    api.actions.saveDailyReview({
      id: today,
      date: today,
      doneTaskIds,
      deferredTaskIds,
      reflection: reflection.trim(),
      energy,
      createdAt: new Date().toISOString(),
    });

    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="evening-shutdown-title">
      <div ref={panelRef} className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-brand-100 text-brand-600">
            <MoonStar className="h-5 w-5" />
          </span>
          <div>
            <h2 id="evening-shutdown-title" className="font-display text-[18px] font-bold">Đóng ngày / Review</h2>
            <p className="text-[12px] text-ink-500">Tick xong · dời việc · ghi cảm nhận</p>
          </div>
          <button className="ml-auto rounded-xl p-1.5 hover:bg-ink-100" onClick={onClose} aria-label="Đóng">
            <X className="h-4 w-4 text-ink-500" />
          </button>
        </div>

        {/* Task review list */}
        <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-500">Việc hôm nay</p>
        {tasks.length === 0 ? (
          <p className="py-4 text-center text-[13px] text-ink-400">Không có việc nào hôm nay.</p>
        ) : (
          <div className="mb-4 max-h-56 space-y-2 overflow-y-auto pr-1">
            {tasks.map((task) => {
              const s = taskStates[task.id] ?? { done: false, defer: false, deferDate: addDaysIso(1) };
              return (
                <EveningTaskReviewRow
                  key={task.id}
                  task={task}
                  state={s}
                  today={today}
                  onDone={(v) => setTaskDone(task.id, v)}
                  onDefer={(v) => setTaskDefer(task.id, v)}
                  onDeferDate={(v) => setDeferDate(task.id, v)}
                />
              );
            })}
          </div>
        )}

        {/* Reflection textarea */}
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-widest text-ink-500">Cảm nhận hôm nay</p>
        <textarea
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder="Hôm nay bạn học được gì? Điều gì diễn ra tốt?"
          className="w-full resize-none rounded-2xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-[13px] outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-200"
          rows={3}
        />

        {/* Energy rating 1-5 */}
        <div className="mt-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-500">Năng lượng (tuỳ chọn)</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setEnergy(energy === n ? undefined : n)}
                title={ENERGY_LABELS[n]}
                className={`flex-1 rounded-xl py-1.5 text-[13px] font-bold transition ${
                  energy === n ? "bg-brand-500 text-white" : "bg-ink-100 text-ink-600 hover:bg-brand-100"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          {energy !== undefined && (
            <p className="mt-1 text-center text-[12px] text-ink-500">{ENERGY_LABELS[energy]}</p>
          )}
        </div>

        {/* Footer actions */}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-ink-200 px-4 py-2 text-[13px] font-semibold hover:bg-ink-50">
            Huỷ
          </button>
          <button onClick={save} className="gbrand rounded-xl px-4 py-2 text-[13px] font-bold text-white">
            Lưu review
          </button>
        </div>
      </div>
    </div>
  );
}
